import { Link } from 'react-router-dom'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { credits } from '../../api/fee'
import type { AllUsersCredit } from '../../api/types'
import { PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { formatCredit } from '../../lib/format'

type Row = AllUsersCredit['items'][number]

/** Port of `AdminCreditListComponent` (`projects/:projectId/admin/credits`):
 * every user × currency balance (`GET …/admin/credit/allusers`). A row with
 * any negative balance is highlighted; each cell links to that user's
 * credit page (with `&currency=`). Read-only. */
export function AdminCreditListPage() {
  const { projectId, project } = useProject()
  const q = useApiQuery(() => credits.allUsers(projectId), [projectId])
  const base = `/projects/${encodeURIComponent(projectId)}/admin/users/credit`
  const currencies = q.data?.currencies ?? []

  const columns: Column<Row>[] = [
    {
      key: 'email',
      label: 'Email',
      format: (_v, row) => <Link to={`${base}?email=${encodeURIComponent(row.email)}`}>{row.email}</Link>,
    },
    ...currencies.map<Column<Row>>((c) => ({
      key: `bal_${c}`,
      label: c === 'credit' ? 'credit (USD)' : c,
      align: 'right',
      numeric: true,
      sortValue: (row) => row.balances?.[c] ?? null,
      cellClass: (_v, row) => {
        const v = row.balances?.[c]
        if (v === undefined || v === null) return 'fs-cell--muted'
        if (v < 0) return 'fs-cell--negative'
        if (v > 0) return 'fs-cell--positive'
        return undefined
      },
      format: (_v, row) => {
        const v = row.balances?.[c]
        if (v === undefined || v === null) return ''
        return (
          <Link to={`${base}?email=${encodeURIComponent(row.email)}&currency=${encodeURIComponent(c)}`}>
            {formatCredit(v, c)}
          </Link>
        )
      },
    })),
  ]

  return (
    <div className="portal-card">
      <PageTitle title={`Credits — ${project.name}`} />
      <QueryState loading={q.loading} error={q.error}>
        <div className="portal-table-scroll">
          <Table
            columns={columns}
            rows={q.data?.items ?? []}
            rowKey={(r) => r.email}
            initialSort={{ key: 'email', dir: 'asc' }}
            rowClassName={(row) => (Object.values(row.balances ?? {}).some((v) => v < 0) ? 'fs-row--debt' : undefined)}
          />
        </div>
      </QueryState>
    </div>
  )
}
